import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { ConfigProvider, Menu } from 'antd';
import Logo from 'assets/images/hp-logo.png';
import { readRememberedAudience, useCurrentRoles } from 'hooks/use-current-roles';
import { sidebarMenuTheme } from 'theme/antd-theme';
import {
  allowsAudience,
  findNav,
  HOME_ICON,
  HOME_ITEM,
  isVisibleFor,
  NAV_GROUPS,
} from '../nav-config';
import { usePendingCounts } from '../pending-counts';
import styles from './styles.module.scss';

interface Props {
  collapsed: boolean;
}

/**
 * Ô đếm việc chờ. Không dùng <Badge> của antd: số của nó có hiệu ứng cuộn và bị kẹt
 * nửa chừng khi mục menu vừa được chọn (số chỉ hiện một nửa). Ô tĩnh này luôn hiện đủ.
 */
const CountPill = ({ count, onIcon }: { count: number; onIcon?: boolean }) => (
  <span className={onIcon ? `${styles.countPill} ${styles.countOnIcon}` : styles.countPill}>
    {count > 99 ? '99+' : count}
  </span>
);

/**
 * Thanh bên nền tối (tách hẳn vùng menu với vùng nội dung, giống cảm giác trang
 * Quản trị cũ). Mỗi nhóm thu gọn được; mở nhóm này thì nhóm khác tự đóng (accordion).
 * Nhóm đang đóng vẫn báo tổng số việc chờ bên trong; mở ra thì số hiện cạnh từng mục.
 * Mỗi mục là <Link> thật → chuột giữa / Ctrl+click mở tab mới được.
 */
const MenuHp = ({ collapsed }: Props) => {
  const location = useLocation();
  const { isAdmin, loaded, canImportData, canViewOnlineReward, canUseEquipmentQuote, audience } =
    useCurrentRoles();
  const pending = usePendingCounts();
  // Chưa biết vai trò: dùng người xem đã nhớ ở lần trước (không có → như MOD) để menu không nháy.
  const viewer = loaded ? audience : readRememberedAudience() ?? 'MOD';
  const isStudioViewer = viewer === 'STUDIO';

  const match = findNav(location.pathname);
  const activeGroup = match?.group?.key;
  // Trang con / trang cũ nay là tab → đánh dấu mục cha trên thanh bên. Trang ẩn không có
  // mục cha (Ghép Idol, Duyệt Idol) thì không tô mục nào — key '' là của "Tổng quan".
  // Quản lý Studio không thấy mục cha "Studio" (danh sách) → Chi tiết Studio tô "Studio của tôi".
  const selectedKeys = match
    ? match.item.hidden
      ? match.parentItem
        ? allowsAudience(match.parentItem, loaded && isAdmin, viewer)
          ? [match.parentItem.key]
          : isStudioViewer && match.group?.key === 'studio-center'
          ? ['studio']
          : []
        : []
      : [match.item.key]
    : [];

  // Chỉ một nhóm mở tại một thời điểm; đổi trang sang nhóm khác thì mở nhóm đó.
  const [openKey, setOpenKey] = useState<string | undefined>(activeGroup);
  useEffect(() => {
    if (activeGroup) setOpenKey(activeGroup);
  }, [activeGroup]);

  const items: MenuProps['items'] = useMemo(() => {
    const countBadge = (count?: number) => (count ? <CountPill count={count} /> : undefined);

    // Quản lý Studio không có trang "Tổng quan" (RoleGate chuyển "/" sang /studio).
    const list: NonNullable<MenuProps['items']> = isStudioViewer
      ? []
      : [
          {
            key: HOME_ITEM.key,
            icon: HOME_ICON,
            label: <Link to="/">{HOME_ITEM.label}</Link>,
          },
        ];
    NAV_GROUPS.forEach((group) => {
      // Chưa biết vai trò thì tạm ẩn mục chỉ-ADMIN để MOD không kịp bấm vào. Mục cần quyền
      // riêng ("Nhập dữ liệu") cũng vậy: canImportData là false cho tới khi biết chắc.
      const visible = group.items.filter((item) =>
        isVisibleFor(
          item,
          loaded && isAdmin,
          { dataImport: canImportData, onlineReward: canViewOnlineReward, equipmentQuote: canUseEquipmentQuote },
          viewer
        )
      );
      if (!visible.length) return;
      const groupCount = visible.reduce((sum, item) => sum + (pending.byKey[item.key] || 0), 0);
      // Nhóm đang đóng mà có việc chờ: số nằm ở GÓC BIỂU TƯỢNG nhóm (như huy hiệu trên
      // thanh tab của app) — không đặt cạnh mũi tên xổ xuống nên không bao giờ đè lên nó,
      // và vẫn thấy được khi thanh bên thu gọn. Mở nhóm ra thì số chuyển sang từng mục.
      const showGroupCount = groupCount > 0 && (collapsed || openKey !== group.key);
      list.push({
        key: group.key,
        icon: showGroupCount ? (
          <span className={styles.iconWithCount}>
            {group.icon}
            <CountPill count={groupCount} onIcon />
          </span>
        ) : (
          group.icon
        ),
        label: group.label,
        // Menu bật ra khi thu gọn nằm trong portal ở <body> → cần class riêng để nhận kiểu ô đếm.
        popupClassName: styles.navPopup,
        children: visible.map((item) => ({
          key: item.key,
          icon: item.icon,
          label: <Link to={`/${item.key}`}>{item.label}</Link>,
          extra: countBadge(pending.byKey[item.key]),
        })),
      });
    });
    return list;
  }, [
    isAdmin,
    loaded,
    canImportData,
    canViewOnlineReward,
    canUseEquipmentQuote,
    viewer,
    isStudioViewer,
    pending.byKey,
    openKey,
    collapsed,
  ]);

  const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const opened = (keys as string[]).find((key) => key !== openKey);
    setOpenKey(opened);
  };

  return (
    <div className={styles.menu}>
      <Link to={isStudioViewer ? '/studio' : '/'} className={styles.boxLogo}>
        <img src={Logo} alt="HP Media" />
        {!collapsed && (
          <span className={styles.brandText}>
            <strong>HP Media</strong>
            <small>Quản trị</small>
          </span>
        )}
      </Link>
      <ConfigProvider theme={sidebarMenuTheme}>
        <Menu
          theme="dark"
          mode="inline"
          inlineCollapsed={collapsed}
          inlineIndent={18}
          openKeys={collapsed ? undefined : openKey ? [openKey] : []}
          onOpenChange={onOpenChange}
          selectedKeys={selectedKeys}
          items={items}
          className={styles.navMenu}
        />
      </ConfigProvider>
    </div>
  );
};

export default MenuHp;
